import Link from 'next/link';

import { cn } from '@utils';
import { isExternalStoryblokLink, resolveStoryblokLinkHref } from '@utils/resolveStoryblokLinkHref';

import { Chevron, isNavigationItemActive, isPathActive } from './SiteHeader.shared';
import type { SiteSettingsData } from './types';

type SiteHeaderDesktopNavigationProps = {
  align?: 'end' | 'start';
  ariaLabel: string;
  headerIsSolid: boolean;
  items: SiteSettingsData['navigation'];
  pathname: string;
};

type SiteHeaderDesktopNavigationItemProps = {
  headerIsSolid: boolean;
  item: SiteSettingsData['navigation'][number];
  pathname: string;
};

function SiteHeaderDesktopNavigationItem({
  headerIsSolid,
  item,
  pathname,
}: SiteHeaderDesktopNavigationItemProps) {
  const href = resolveStoryblokLinkHref(item.href);
  const hasChildren = item.children != null && item.children.length > 0;
  const isActive = isNavigationItemActive(item, pathname);

  if (!hasChildren) {
    return (
      <Link
        className={cn(
          'rounded-full px-4 py-2 text-sm uppercase tracking-[0.18em] transition-colors duration-200',
          isActive && headerIsSolid && 'bg-white text-primary',
          isActive && !headerIsSolid && 'text-white',
          !isActive && headerIsSolid && 'text-primary hover:text-accent',
          !isActive && !headerIsSolid && 'text-white/80 hover:text-white',
        )}
        href={href}
      >
        {item.label}
      </Link>
    );
  }

  return (
    <div className="group relative">
      <Link
        className={cn(
          'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm uppercase tracking-[0.18em] transition-colors duration-200',
          isActive && headerIsSolid && 'bg-white text-primary',
          isActive && !headerIsSolid && 'text-white',
          !isActive && headerIsSolid && 'text-primary hover:text-accent',
          !isActive && !headerIsSolid && 'text-white/80 hover:text-white',
        )}
        href={href}
      >
        {item.label}
        <Chevron />
      </Link>

      <div className="pointer-events-none absolute right-0 top-full pt-3 opacity-0 transition-all duration-200 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
        <div className="min-w-72 rounded-[1.5rem] border border-primary/10 bg-white p-3 text-primary shadow-[0_28px_80px_-40px_rgba(114,106,100,0.6)]">
          {item.children?.map((child) => {
            const childHref = resolveStoryblokLinkHref(child.href);
            const childIsExternal = isExternalStoryblokLink(child.href);

            return (
              <Link
                className={cn(
                  'block rounded-[1rem] px-4 py-3 transition-colors duration-200',
                  isPathActive(pathname, childHref)
                    ? 'bg-muted text-primary'
                    : 'text-primary hover:bg-[#f7f4ef] hover:text-accent',
                )}
                href={childHref}
                key={child.label}
                rel={childIsExternal ? 'noreferrer noopener' : undefined}
                target={childIsExternal ? '_blank' : undefined}
              >
                <span className="block text-sm uppercase tracking-[0.16em]">{child.label}</span>
                {child.description && (
                  <span className="mt-1 block text-sm normal-case tracking-normal text-primary">
                    {child.description}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function SiteHeaderDesktopNavigation({
  align = 'start',
  ariaLabel,
  headerIsSolid,
  items,
  pathname,
}: SiteHeaderDesktopNavigationProps) {
  return (
    <nav
      aria-label={ariaLabel}
      className={cn('flex items-center gap-2', align === 'end' && 'justify-end')}
    >
      {items.map((item) => (
        <SiteHeaderDesktopNavigationItem
          headerIsSolid={headerIsSolid}
          item={item}
          key={item.label}
          pathname={pathname}
        />
      ))}
    </nav>
  );
}
